import {useEffect, useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import {getStoredConsent, getRegionModel, PRIVACY_POLICY_URL} from 'consent/consentManager';

const CATEGORIES = [
    {
        key: 'necessary',
        locked: true,
        title: 'Strictly necessary',
        description: 'Required for the site to work: security and remembering your cookie choices. These are always on and cannot be disabled.',
    },
    {
        key: 'analytics',
        title: 'Analytics',
        description: 'Google Analytics — helps us understand how the site is used (pages visited, feature interest) so we can improve it.',
    },
    {
        key: 'marketing',
        title: 'Marketing & advertising',
        description: 'Google Ads and Awin affiliate/partner tracking used to measure ad campaigns. Turning this off also acts as your "Do Not Sell or Share My Personal Information" opt-out (CCPA/CPRA).',
    },
];

const CookiePreferencesDialog = ({open, onClose, onSave, onAcceptAll, onRejectAll}) => {
    const [toggles, setToggles] = useState({analytics: false, marketing: false});
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!open) return;
        const stored = getStoredConsent();
        if (stored) {
            setToggles({
                analytics: Boolean(stored.categories.analytics),
                marketing: Boolean(stored.categories.marketing),
            });
        } else {
            // No decision yet: EU/unknown start OFF (opt-in), US starts ON (opt-out).
            const defaultsOn = getRegionModel() === 'optout';
            setToggles({analytics: defaultsOn, marketing: defaultsOn});
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={fullScreen}
            maxWidth="sm"
            aria-labelledby="cookie-preferences-title"
        >
            <DialogTitle
                id="cookie-preferences-title"
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5}}
            >
                Cookie preferences
                <IconButton aria-label="Close cookie preferences" onClick={onClose} edge="end">
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Choose which cookies Younet may use. You can change your choice at any time
                    via "Cookie Settings" in the footer. Learn more in our{' '}
                    <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">Privacy policy</Link>.
                </Typography>

                {CATEGORIES.map((category, index) => (
                    <Box key={category.key}>
                        {index > 0 && <Divider sx={{my: 1.5}}/>}
                        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="subtitle2">
                                    {category.title}
                                    {category.locked && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>
                                            Always on
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                </Typography>
                            </Box>
                            <Switch
                                checked={category.locked ? true : toggles[category.key]}
                                disabled={category.locked}
                                onChange={(event) => setToggles((prev) => ({...prev, [category.key]: event.target.checked}))}
                                inputProps={{'aria-label': `${category.title} cookies`}}
                            />
                        </Box>
                    </Box>
                ))}
            </DialogContent>
            <DialogActions
                sx={{
                    px: {xs: 2, sm: 3},
                    py: 2,
                    pb: {xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 2},
                    gap: 1,
                    // Primary action first on mobile (column-reverse), rightmost on desktop
                    flexDirection: {xs: 'column-reverse', sm: 'row'},
                    alignItems: 'stretch',
                    '& > :not(:first-of-type)': {ml: {xs: 0, sm: 1}},
                }}
            >
                <Button variant="outlined" fullWidth={fullScreen} onClick={onRejectAll}>Reject all</Button>
                <Button variant="outlined" fullWidth={fullScreen} onClick={onAcceptAll}>Accept all</Button>
                <Button variant="contained" fullWidth={fullScreen} onClick={() => onSave(toggles)}>Save preferences</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CookiePreferencesDialog;
